import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import React, { useEffect, useMemo, useState } from 'react';

import { LOB } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useCurrentMember } from '../../../../hooks/useCurrentMember';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSupportedLanguages } from '../../../../hooks/useSupportedLanguages';
import {
  getLanguageFromCookie,
  getLobFromCookie,
} from '../Internationalization/helpers';
import { Locale } from '../Internationalization/types';
import {
  languageNotSupportedModalInfoStyles,
  languageNotSupportedModalStyles,
  languageNotSupportedModalTitleStyles,
} from './LanguageNotSupported.styled';

export const LanguageNotSupportedModal = () => {
  const currentMember = useCurrentMember();
  const [isOpen, setIsOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const data = useLagoon('ui-messaging')();
  const languageModalTitle = find(data, {
    key: ConstantsLagoon.LANGUAGE_MODAL.LANGUAGE_NOT_AVAILABLE,
  });
  const languageModalContent = find(data, {
    key: ConstantsLagoon.LANGUAGE_MODAL.LANGUAGE_NOT_AVAILABLE_CONTENT,
  });
  const viewIdCardButton = find(data, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_BUTTON,
  });

  const handleViewIdCard = () => {
    window.location.href =
      '/community/dashboard/modal/id-cards?goBackOnClose=1';
  };

  const supportedLanguages = useSupportedLanguages();

  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const urlLocale = searchParams?.get('locale');

  const lob = useMemo(() => {
    const lobFromCookie = getLobFromCookie();

    return (currentMember?.lineOfBusiness || lobFromCookie || '').toUpperCase();
  }, [currentMember?.lineOfBusiness]);

  useEffect(() => {
    let language = getLanguageFromCookie() || urlLocale;
    if (
      !language ||
      ['undefined', 'null', ''].includes(language?.toLowerCase()?.trim())
    ) {
      language = Locale.enUS;
    }
    setShowModal(!supportedLanguages.includes(language as Locale));
  }, [supportedLanguages]);

  if (!(showModal && lob === LOB.CNS)) return null;

  return (
    <React.Fragment>
      <Modal
        css={languageNotSupportedModalStyles}
        data-auto-testid="language-not-supported-content-modal"
        data-testid="language-not-supported-content-modal"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        /* Title is added to prevent JS error as the component required title prop, title is hidden adding css rules for abyss-modal-header-container */
        title="Language Not Supported Modal"
      >
        <Layout.Stack
          css={{
            alignContent: 'center',
            paddingTop: 'clamp(15px, 3vh, 40px)',
            paddingBottom: 'clamp(15px, 3vh, 40px)',
            marginTop: 0,
            marginBottom: 0,
          }}
          space={15}
        >
          <Layout.Stack css={{ alignContent: 'center' }}>
            <IconBrand
              css={{ margin: '0px' }}
              icon="document_multiple"
              size="70px"
            />{' '}
            <Heading
              css={languageNotSupportedModalTitleStyles}
              data-auto-testid="heading-language-not-supported-modal"
              data-testid="heading-language-not-supported-modal"
            >
              {languageModalTitle?.message}
            </Heading>
            <Modal.Section
              css={languageNotSupportedModalInfoStyles}
              data-auto-testid="language-not-supported-content-info"
              data-testid="language-not-supported-content-info"
            >
              {languageModalContent?.message}
            </Modal.Section>
          </Layout.Stack>
          <Modal.Section css={{ paddingBottom: 0 }}>
            <Button
              css={{
                minWidth: '138px',
                maxWidth: '300px',
                height: '48px',
              }}
              data-auto-testid="language-not-supported-view-id-button"
              data-testid="language-not-supported-view-id-button"
              onClick={handleViewIdCard}
              size="$sm"
            >
              {viewIdCardButton?.message}
            </Button>
          </Modal.Section>
        </Layout.Stack>
      </Modal>
    </React.Fragment>
  );
};
